import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, useWatch } from 'react-hook-form';

import { DateRangePicker, TimePicker } from '@/components/common/date-picker';
import { Drawer } from '@/components/feedback/drawer';
import { MultiSelectField } from '@/components/forms';
import { appTzLabel, fromDateRange, fromTime, toDayjs, toDayjsRange } from '@/lib/date';

import {
  customerActivityLogToDefaults,
  customerActivityLogToFilters,
} from '../activity-log-filters';
import { CUSTOMER_ACTIVITY_TYPE_LABEL } from '../format';
import { customerActivityLogFiltersSchema } from '../schemas';
import type { CustomerActivityLogFilterFormValues, CustomerActivityLogFilters } from '../types';

import styles from './customer-activity-log-filter-drawer.module.scss';

const TYPE_OPTIONS = (
  Object.keys(CUSTOMER_ACTIVITY_TYPE_LABEL) as (keyof typeof CUSTOMER_ACTIVITY_TYPE_LABEL)[]
).map((value) => ({ value, label: CUSTOMER_ACTIVITY_TYPE_LABEL[value] }));

export type CustomerActivityLogFilterDrawerProps = {
  open: boolean;
  /** Last applied filters; the draft re-seeds from this on each open. */
  value: CustomerActivityLogFilters;
  onApply: (filters: CustomerActivityLogFilters) => void;
  onClose: () => void;
};

type BodyProps = Omit<CustomerActivityLogFilterDrawerProps, 'open'>;

function FilterBody({ value, onApply, onClose }: BodyProps) {
  const draft = customerActivityLogToDefaults(value);
  const form = useForm<CustomerActivityLogFilterFormValues>({
    resolver: zodResolver(customerActivityLogFiltersSchema),
    defaultValues: {
      type: draft.type ?? [],
      logDateFrom: draft.logDateFrom ?? null,
      logDateTo: draft.logDateTo ?? null,
      logTimeFrom: draft.logTimeFrom ?? null,
      logTimeTo: draft.logTimeTo ?? null,
    },
    mode: 'onChange',
  });

  const [dateFrom, dateTo, timeFrom, timeTo] = useWatch({
    control: form.control,
    name: ['logDateFrom', 'logDateTo', 'logTimeFrom', 'logTimeTo'],
  });
  const dateError = form.formState.errors.logDateTo?.message;
  const timeError = form.formState.errors.logTimeTo?.message;

  const handleApply = form.handleSubmit((values) => {
    onApply(customerActivityLogToFilters(values));
    onClose();
  });

  return (
    <Drawer
      open
      onClose={onClose}
      title="Filter"
      width={400}
      primaryText="Apply"
      onPrimary={handleApply}
      primaryDisabled={Boolean(dateError || timeError)}
      cancelText="Cancel"
      onCancel={onClose}
    >
      <MultiSelectField
        name="type"
        control={form.control}
        label="Type"
        placeholder="Select"
        options={TYPE_OPTIONS}
      />

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>{appTzLabel('Log Date Range')}</legend>
        <DateRangePicker
          aria-label={appTzLabel('Log Date Range')}
          format="MM/DD/YYYY"
          allowEmpty={[true, true]}
          style={{ width: '100%' }}
          status={dateError ? 'error' : undefined}
          value={toDayjsRange(dateFrom && dateTo ? [dateFrom, dateTo] : null)}
          onChange={(range) => {
            const iso = fromDateRange(range);
            form.setValue('logDateFrom', iso?.[0] ?? null, { shouldValidate: true });
            form.setValue('logDateTo', iso?.[1] ?? null, { shouldValidate: true });
          }}
        />
        {dateError ? <p className={styles.error}>{dateError}</p> : null}
      </fieldset>

      <fieldset className={styles.fieldset}>
        <legend className={styles.legend}>Log Time Range</legend>
        <div className={styles.timeRow}>
          <TimePicker
            aria-label="Log Time From"
            format="HH:mm"
            placeholder="From"
            style={{ width: '100%' }}
            status={timeError ? 'error' : undefined}
            value={toDayjs(timeFrom, 'HH:mm') ?? null}
            onChange={(t) =>
              form.setValue('logTimeFrom', fromTime(t) ?? null, { shouldValidate: true })
            }
          />
          <TimePicker
            aria-label="Log Time To"
            format="HH:mm"
            placeholder="To"
            style={{ width: '100%' }}
            status={timeError ? 'error' : undefined}
            value={toDayjs(timeTo, 'HH:mm') ?? null}
            onChange={(t) =>
              form.setValue('logTimeTo', fromTime(t) ?? null, { shouldValidate: true })
            }
          />
        </div>
        {timeError ? <p className={styles.error}>{timeError}</p> : null}
      </fieldset>
    </Drawer>
  );
}

export function CustomerActivityLogFilterDrawer({
  open,
  ...rest
}: CustomerActivityLogFilterDrawerProps) {
  if (!open) {
    return (
      <Drawer open={false} onClose={rest.onClose}>
        {null}
      </Drawer>
    );
  }
  // Unmount on close; remounts fresh on open so the draft re-seeds from the last applied `value`.
  return <FilterBody {...rest} />;
}
